import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { agreementApi } from "@/services/api";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Eye,
  Trash2,
  ClipboardList,
  Car,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  PlayCircle
} from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  active: {
    label: "Active",
    className: "bg-success-light text-success border-success/20",
    icon: PlayCircle
  },
  completed: {
    label: "Completed",
    className: "bg-primary/10 text-primary border-primary/20",
    icon: CheckCircle2
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-destructive-light text-destructive border-destructive/20",
    icon: XCircle
  }
};

export default function AgreementsListPage() {
  const navigate = useNavigate();
  
  const [agreements, setAgreements] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgreements, setTotalAgreements] = useState(0);
  const [pageSize] = useState(10);

  const fetchAgreements = useCallback(async () => {
    try {
      setLoading(true);
      const data = await agreementApi.getAgreements(currentPage, pageSize, searchQuery, statusFilter);
      setAgreements(data.agreements);
      setTotalPages(data.totalPages);
      setTotalAgreements(data.total);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error("Failed to load agreements", {
        description: "Please check your connection and try again."
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await agreementApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const seedDatabase = async () => {
    try {
      const result = await agreementApi.seedAgreements();
      toast.success("Database Seeded", { description: result.message });
      fetchAgreements();
      fetchStats();
    } catch (error) {
      console.error('Error seeding database:', error);
    }
  };

  useEffect(() => {
    fetchAgreements();
    fetchStats();
  }, [fetchAgreements, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchAgreements();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter]);

  const handleCreateAgreement = async () => {
    try {
      const newAgreement = await agreementApi.createAgreement();
      toast.success("Agreement Created", {
        description: `New agreement ${newAgreement.id} has been created.`
      });
      navigate(`/agreement/${newAgreement.id}?edit=true`);
    } catch (error) {
      toast.error("Failed to create agreement", {
        description: "Please try again."
      });
    }
  };

  const handleDeleteAgreement = async (id) => {
    try {
      await agreementApi.deleteAgreement(id);
      toast.success("Agreement Deleted", {
        description: `Agreement ${id} has been deleted.`
      });
      fetchAgreements();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete agreement", {
        description: "Please try again."
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader onCreateAgreement={handleCreateAgreement} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Rental Agreements</h1>
            <p className="text-muted-foreground mt-1">Manage vehicle rental contracts and agreements</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={seedDatabase} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Seed Data
            </Button>
            <Button onClick={handleCreateAgreement} className="gap-2">
              <Plus className="w-4 h-4" />
              New Agreement
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Total Agreements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-success" />
                Active
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-success">{stats.active}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Completed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-primary">{stats.completed}</p>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-destructive" />
                Cancelled
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-destructive">{stats.cancelled}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by renter name, email, or license plate..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Agreements Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Agreements</CardTitle>
            <CardDescription>
              {totalAgreements} agreement{totalAgreements !== 1 ? 's' : ''} found
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="font-semibold">Agreement</TableHead>
                    <TableHead className="font-semibold">Renter</TableHead>
                    <TableHead className="font-semibold hidden md:table-cell">Vehicle</TableHead>
                    <TableHead className="font-semibold hidden lg:table-cell">Rental Period</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                          <p className="text-muted-foreground">Loading agreements...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : agreements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                          <ClipboardList className="w-12 h-12 opacity-30" />
                          <p>No agreements found</p>
                          <Button variant="outline" size="sm" onClick={handleCreateAgreement}>
                            Create your first agreement
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    agreements.map((agreement, index) => {
                      const StatusIcon = statusConfig[agreement.agreementInfo?.status]?.icon || PlayCircle;
                      
                      return (
                        <TableRow
                          key={agreement.id}
                          className={`cursor-pointer transition-colors ${index % 2 === 1 ? 'bg-muted/30' : ''}`}
                          onClick={() => navigate(`/agreement/${agreement.id}`)}
                        >
                          <TableCell className="font-mono font-medium text-primary">
                            #{agreement.id}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-foreground">{agreement.renterInfo?.name || 'Unnamed Renter'}</p>
                              <p className="text-xs text-muted-foreground">{agreement.renterInfo?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <Car className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">
                                  {agreement.vehicleInfo?.year} {agreement.vehicleInfo?.make} {agreement.vehicleInfo?.model}
                                </p>
                                <p className="text-xs text-muted-foreground">{agreement.vehicleInfo?.licensePlate}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-muted-foreground">
                            <div className="text-sm">
                              <p>{formatDate(agreement.rentalPeriod?.pickupDate)}</p>
                              <p className="text-xs">to {formatDate(agreement.rentalPeriod?.returnDate)}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`gap-1 ${statusConfig[agreement.agreementInfo?.status]?.className || ''}`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig[agreement.agreementInfo?.status]?.label || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => navigate(`/agreement/${agreement.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Agreement</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete agreement #{agreement.id}? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteAgreement(agreement.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalAgreements)} of {totalAgreements} agreements
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="w-8 h-8 p-0"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
